import { useEffect, useState, useContext, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import { SkeletonRow } from './Skeleton';
import { API_BASE_URL, mediaUrl } from './config';
import { IconWave, IconPackage, IconCreditCard, IconGift, IconHeartFilled, IconArrowRight, IconRotateCcw } from './Icons';
import { formatCurrency } from './currency';

const STATUS_LABELS = {
  received: 'Received', processing: 'Processing', shipped: 'Shipped',
  delivered: 'Delivered', cancelled: 'Cancelled', pending: 'Pending', confirmed: 'Confirmed',
};

export default function Dashboard({ onAddToCart, onNavigate, onNavigateHome }) {
  const { user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [rewards, setRewards] = useState({ points: 0, dollarValue: 0 });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    // Signed-in check, not a token check -- see AuthContext (httpOnly cookie).
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const [ordersRes, wishlistRes, rewardsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/orders/my-orders`, {}),
        fetch(`${API_BASE_URL}/api/wishlist`, {}),
        fetch(`${API_BASE_URL}/api/rewards`, {}),
      ]);
      const [ordersData, wishlistData, rewardsData] = await Promise.all([
        ordersRes.ok ? ordersRes.json() : [],
        wishlistRes.ok ? wishlistRes.json() : [],
        rewardsRes.ok ? rewardsRes.json() : { points: 0, dollarValue: 0 },
      ]);
      setOrders(ordersData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      setWishlist(wishlistData);
      setRewards(rewardsData);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const totalSpent = orders.reduce((sum, o) => sum + (o.total || 0), 0);
  const recentOrders = orders.slice(0, 5);

  // Distinct products from delivered orders, most-recent first, for "Buy Again"
  const buyAgainItems = [];
  const seen = new Set();
  orders.filter(o => o.status === 'delivered').forEach(o => {
    (o.items || []).forEach(item => {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        buyAgainItems.push(item);
      }
    });
  });

  const handleBuyAgain = (item) => {
    if (!onAddToCart) return;
    onAddToCart({ id: item.id, name: item.name, price: item.price, image: item.image }, item.size, item.color);
    onNavigateHome?.();
  };

  if (loading) {
    return (
      <div className="dash-page">
        <SkeletonRow /><SkeletonRow /><SkeletonRow />
      </div>
    );
  }

  return (
    <div className="dash-page">
      <h2 className="dash-greeting">Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''} <IconWave size={22} /></h2>

      <div className="dash-stats-grid">
        <div className="dash-stat-card">
          <span className="dash-stat-icon"><IconPackage size={22} /></span>
          <div>
            <div className="dash-stat-value">{orders.length}</div>
            <div className="dash-stat-label">Total Orders</div>
          </div>
        </div>
        <div className="dash-stat-card">
          <span className="dash-stat-icon"><IconCreditCard size={22} /></span>
          <div>
            <div className="dash-stat-value">{formatCurrency(totalSpent)}</div>
            <div className="dash-stat-label">Total Spent</div>
          </div>
        </div>
        <div className="dash-stat-card">
          <span className="dash-stat-icon"><IconGift size={22} /></span>
          <div>
            <div className="dash-stat-value">{rewards.points} pts</div>
            <div className="dash-stat-label">Reward Points</div>
            <div className="dash-stat-sub">≈ {formatCurrency(rewards.dollarValue)}</div>
          </div>
        </div>
        <div className="dash-stat-card">
          <span className="dash-stat-icon"><IconHeartFilled size={22} /></span>
          <div>
            <div className="dash-stat-value">{wishlist.length}</div>
            <div className="dash-stat-label">Wishlist Items</div>
          </div>
        </div>
      </div>

      <div className="dash-section">
        <div className="dash-section-hdr">
          <h3>Recent Orders</h3>
          <button className="dash-section-link" onClick={() => onNavigate?.('orders')}>View all <IconArrowRight size={12} /></button>
        </div>
        {recentOrders.length === 0 ? (
          <p className="dash-empty">No orders yet.</p>
        ) : (
          <div className="dash-orders-table">
            {recentOrders.map(o => (
              <div key={o.id} className="dash-order-row">
                <span className="dash-order-id">#{o.id.slice(0, 8)}</span>
                <span className="dash-order-date">{new Date(o.createdAt).toLocaleDateString()}</span>
                <span className={`dash-order-status dash-status-${o.status}`}>{STATUS_LABELS[o.status] || o.status}</span>
                <span className="dash-order-total">{formatCurrency(o.total)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {wishlist.length > 0 && (
        <div className="dash-section">
          <div className="dash-section-hdr">
            <h3>From Your Wishlist</h3>
            <button className="dash-section-link" onClick={() => onNavigate?.('wishlist')}>View all <IconArrowRight size={12} /></button>
          </div>
          <div className="dash-card-strip">
            {wishlist.slice(0, 4).map(p => (
              <div key={p.id} className="dash-mini-card">
                <img src={mediaUrl(p.image)} alt={p.name} />
                <span className="dash-mini-name">{p.name}</span>
                <span className="dash-mini-price">{formatCurrency(p.price)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {buyAgainItems.length > 0 && (
        <div className="dash-section">
          <div className="dash-section-hdr">
            <h3>Buy Again</h3>
          </div>
          <div className="dash-card-strip">
            {buyAgainItems.slice(0, 6).map(item => (
              <div key={item.id} className="dash-mini-card">
                <img src={mediaUrl(item.image)} alt={item.name} />
                <span className="dash-mini-name">{item.name}</span>
                <span className="dash-mini-price">{formatCurrency(item.price)}</span>
                <button className="dash-mini-buy-btn" onClick={() => handleBuyAgain(item)}><IconRotateCcw size={13} /> Buy Again</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
